import { Injector } from '@angular/core';
import { ClassicPreset, GetSchemes, NodeEditor } from 'rete';
import { AngularArea2D, AngularPlugin, ControlComponent, Presets as RenderPresets } from 'rete-angular-plugin/15';
import { AreaExtensions, AreaPlugin } from 'rete-area-plugin';
import { ArrangeAppliers, Presets as ArrangePresets, AutoArrangePlugin } from 'rete-auto-arrange-plugin';
import { BidirectFlow, ConnectionPlugin } from 'rete-connection-plugin';
import { ClassicScheme } from 'rete-connection-plugin/_types/types';
import { ContextMenuExtra, ContextMenuPlugin } from 'rete-context-menu-plugin';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { CustomConnectionComponent } from '../connections/components/custom-connection/custom-connection.component';
import { SelectableConnection } from '../connections/models/SelectableConnection';
import { DockPlugin } from '../drag&drop/dock-plugin';
import { Action } from '../models/action';
import { ChangeStepStateActionData } from '../models/change-step-state-action-data';
import { Event } from '../models/event';
import { EventData } from '../models/event-data';
import { ReteContext } from '../models/rete-context';
import { Socket } from '../models/socket';
import { Step } from '../models/step';
import { StepData } from '../models/step-data';
import { StepInput } from '../models/step-input';
import { StepOutput } from '../models/step-output';
import { StepStateChangedTriggerData } from '../models/step-state-change-trigger-data';
import { Trigger } from '../models/trigger';
import { AnyActionData } from '../models/Types/any-action-data';
import { AnyTriggerData } from '../models/Types/any-trigger-data';
import { StepActionData } from '../models/Types/step-action-data';
import { StepTriggerData } from '../models/Types/step-trigger-data';
import { Workflow } from '../models/workflow';
import { CustomNodeComponent } from '../nodes/components/custom-node/custom-node.component';
import { WorkflowActionNodeComponent } from '../nodes/components/workflow-action-node/workflow-action-node.component';
import { WorkflowStepNodeComponent } from '../nodes/components/workflow-step-node/workflow-step-node.component';
import { WorkflowTriggerNodeComponent } from '../nodes/components/workflow-trigger-node/workflow-trigger-node.component';
import { NodeEvent } from '../nodes/models/node-event';
import { NodeEventTypes } from '../nodes/models/node-event-types';
import { WorkflowActionNode } from '../nodes/models/workflow-action-node';
import { WorkflowStepNode } from '../nodes/models/workflow-step-node';
import { WorkflowTriggerNode } from '../nodes/models/workflow-trigger-node';
import { CustomSocketComponent } from '../sockets/custom-socket/custom-socket.component';
import { DesignerCallbackHandler } from './designer-callback-handler';
import { MappingInfos } from './mapping-infos';

class Connection<N extends ClassicPreset.Node> extends ClassicPreset.Connection<N, N> {}
type Schemes = GetSchemes<ClassicPreset.Node, SelectableConnection>;

class AutoArrangeNode extends ClassicPreset.Node {
    width = 180;
    height = 120;
}
type Schemes2 = GetSchemes<AutoArrangeNode, Connection<AutoArrangeNode>>;

type AreaExtra = AngularArea2D<Schemes> | ContextMenuExtra;
type ExtractPayload<T extends ClassicScheme, K extends string> = Extract<
    AngularArea2D<T>,
    {
        type: 'render';
        data: {
            type: K;
        };
    }
>['data'];

export class EditorWrapper {
    /**
     * Instance de l'éditeur Rete.js.
     */
    private editor;

    /**
     * Instance de la zone d'affichage de l'éditeur Rete.js.
     */
    private area;

    /**
     * Instance du plugin utilisé pour le rendu.
     */
    private render: AngularPlugin<Schemes, AreaExtra>;

    private autoArrange: AutoArrangePlugin<Schemes2>;

    /**
     * Contexte d'execution du designer.
     */
    context: ReteContext;

    nextTriggerId: number = 1;

    nextActionId: number = 1;

    nextEventId: number = 1;

    nextStepId: number = 1;

    /**
     * Constructeur.
     * @param injector : Utilisé par l'injection de dépendance.
     * @param callbackHandler : Gestionnaire des évènements du designer de workflow.
     */
    constructor(
        private injector: Injector,
        private callbackHandler: DesignerCallbackHandler,
        private mappingInfos: MappingInfos
    ) {}

    /**
     * Crée l'editeur Rete.js et retourne le contexte d'execution du designer.
     * @param container : L'élément HTML contenant l'editeur Rete.js
     * @returns : Le contexte d'execution du designer.
     */
    public createEditor(container: HTMLElement): ReteContext {
        const context = new ReteContext();
        const socket = new ClassicPreset.Socket('socket');
        this.editor = new NodeEditor<Schemes>();
        this.area = new AreaPlugin<Schemes, AreaExtra>(container);
        this.autoArrange = new AutoArrangePlugin<Schemes2>();
        this.autoArrange.addPreset(ArrangePresets.classic.setup());
        const dock = new DockPlugin<Schemes>();

        context.editor = this.editor;
        context.area = this.area;
        context.dock = dock;

        const connection = new ConnectionPlugin<Schemes, AreaExtra>();
        this.render = new AngularPlugin<Schemes, AreaExtra>({ injector: this.injector });

        /* Initialise les menus contextuels */
        this.initEditorContextMenu();

        /* Active la possibilité de selectionner les noeuds */
        this.initEditorSelection();

        this.customizeEditor();

        //connection.addPreset(ConnectionPresets.classic.setup());
        connection.addPreset(() => new BidirectFlow());

        this.editor.use(this.area);
        this.area.use(connection);
        this.area.use(this.render);
        this.area.use(dock);
        this.area.use(this.autoArrange);

        /* Indique que le noeud selectionné doit être au 1er plan */
        AreaExtensions.simpleNodesOrder(this.area);

        /* Adapte la taille pour voir l'ensemble des noeuds */
        AreaExtensions.zoomAt(this.area, this.editor.getNodes());

        this.context = context;
        return context;
    }

    /**
     * Recentre le designer sur le workflow
     */
    public recenter() {
        AreaExtensions.zoomAt(this.area, this.editor.getNodes());
    }

    /**
     * Centre et zoom le designer sur un noeud.
     */
    public centerOnNode(node: ClassicPreset.Node) {
        AreaExtensions.zoomAt(this.area, [node]);
    }

    /**
     * Centre et zoom le designer sur les noeuds spécifiés.
     */
    public centerOnNodes(nodes: ClassicPreset.Node[]) {
        AreaExtensions.zoomAt(this.area, nodes);
    }

    /**
     * Rearrange les noeuds du designer.
     */
    public rearrange() {
        const applier = new ArrangeAppliers.TransitionApplier<Schemes2, never>({
            duration: 500,
            timingFunction: (t) => t,
            onTick: this.onTick.bind(this),
        });

        const options = {
            'elk.spacing.nodeNode': '100',
            'elk.layered.spacing.nodeNodeBetweenLayers': '100',
        };

        // Applique la taille du rendu sur les noeuds
        this.editor.getNodes().forEach((node) => {
            const nodeView = this.context.area.nodeViews.get(node.id);
            console.log(`${node.label} --> ${nodeView.element.offsetWidth} x ${nodeView.element.offsetHeight}`);
            node.width = nodeView.element.offsetWidth + 1; // Ajoute 1 pour les erreurs d'arrondi
            node.height = nodeView.element.offsetHeight + 1; // Ajoute 1 pour les erreurs d'arrondi
        });

        this.autoArrange.layout({ applier: applier, options: options });
    }

    /**
     * Utilisé pour animer le réarangement des noeuds du designer.
     */
    private async onTick() {
        await AreaExtensions.zoomAt(this.area, this.editor.getNodes());
    }

    /**
     * Souscrit aux évènements emis par l'editeur Rete.js
     */
    private subscribeToEditorEvents() {
        this.area.addPipe(this.handleEditorEvents.bind(this));
    }

    handleEditorEvents(context) {
        //Possibilité de régir a des evenments envoyé par l'editeur en fonction du context.type

        if (context.type === 'connectioncreate') {
            if (!(context.data instanceof SelectableConnection)) {
                // Cas d'une connexion venant d'être créée par l'utilisateur --> On ajoute la connexion nous même pour avoir une instance de SelectableConnection.
                const eventData = new EventData();
                eventData.id = this.generateNewEventId();
                eventData.conditions = [];
                eventData.isEnabled = true;
                const triggerNode = this.context.editor.getNodes().find((x) => x.id === context.data.source);
                const actionNode = this.context.editor.getNodes().find((x) => x.id === context.data.target);
                const sourceSocket = new Socket();
                sourceSocket.code = (context.data as any).sourceOutput;
                sourceSocket.label = triggerNode.outputs[sourceSocket.code].socket.name;

                const targetSocket = new Socket();
                targetSocket.code = (context.data as any).targetInput;
                targetSocket.label = actionNode.inputs[targetSocket.code].socket.name;

                if (
                    this.context.editor
                        .getConnections()
                        .find((x) => x.source === triggerNode.id && x.target == actionNode.id && x.sourceOutput === sourceSocket.code && x.targetInput === targetSocket.code)
                ) {
                    // Une connexion identique existe déjà --> on l'ignore
                    console.warn('Une connexion identique existe déja --> abandon de la création de la connexion.');
                    return;
                }

                this.addConnection(triggerNode.id, sourceSocket, actionNode.id, targetSocket, eventData.id, eventData);
                // On ne retourne pas le contexte pour que l'editeur ne gère pas la création de la connexion.
                return;
            }
        }

        return context;
    }

    /**
     * Personnalise les elements de l'editeur Rete.js
     */
    private customizeEditor() {
        this.render.addPreset(
            RenderPresets.classic.setup({
                customize: {
                    control: (data) => {
                        return this.customizeEditorControls(data);
                    },
                    node: (data) => {
                        return this.customizeEditorNodes(data);
                    },
                    socket: (data) => {
                        return this.customizeEditorSockets(data);
                    },
                    connection: (data) => {
                        return this.customizeEditorConnections(data);
                    },
                },
            })
        );
    }

    /**
     * Personnalise les sockets des noeuds affichés dans l'éditeur.
     * @param data : Les données de la socket permettant de définir le composant à utiliser pour afficher la socket.
     * @returns Le composant utilisé pour affiché la socket.
     */
    private customizeEditorSockets(data: ExtractPayload<Schemes, 'socket'>) {
        return CustomSocketComponent;
    }

    /**
     * Personnalise les connexions affichés dans l'éditeur.
     * @param data : Les données de la connexion permettant de définir le composant à utiliser pour afficher la connexion.
     * @returns Le composant utilisé pour affiché la connexion.
     */
    private customizeEditorConnections(data: ExtractPayload<Schemes, 'connection'>) {
        if ((data.payload as any).isPseudo) {
            // Cas d'une connection en cours de création --> on ne fait rien
        } else if (data.payload instanceof SelectableConnection) {
            // Cas d'une connection finale --> ajout d'un gestionnaire d'évènement sur le clic
            data.payload.clicked = this.callbackHandler.onConnectionClick.bind(this.callbackHandler);
        } else {
            console.warn('Type de connexion non attendue');
        }
        return CustomConnectionComponent;
    }

    /**
     * Personnalise les noeuds affichés dans l'éditeur.
     * @param data : Les données du noeud permettant de définir le composant à utiliser pour afficher le noeud.
     * @returns Le composant utilisé pour affiché le noeud.
     */
    private customizeEditorNodes(data: ExtractPayload<Schemes, 'node'>) {
        //console.log('node', data);
        if (data.payload instanceof WorkflowStepNode) {
            return WorkflowStepNodeComponent;
        } else if (data.payload instanceof WorkflowTriggerNode) {
            return WorkflowTriggerNodeComponent;
        } else if (data.payload instanceof WorkflowActionNode) {
            return WorkflowActionNodeComponent;
        } else {
            return CustomNodeComponent;
        }
    }

    /**
     * Personnalise les contrôles (input, boutons ...) affichés dans les noeuds affichés dans l'éditeur.
     * @param data : Les données du contrôle permettant de définir le composant à utiliser pour afficher le contrôle.
     * @returns Le composant utilisé pour affiché le contrôle.
     */
    private customizeEditorControls(data: ExtractPayload<Schemes, 'control'>) {
        if (data.payload instanceof ClassicPreset.InputControl) {
            // don't forget to explicitly specify the built-in ControlComponent
            return ControlComponent;
        } else if (data.payload) {
            return ControlComponent;
        } else {
            return null;
        }
    }

    /**
     * Initialise le menu contextuel de l'editeur Rete.js.
     */
    private initEditorContextMenu() {
        const contextMenu = new ContextMenuPlugin<Schemes>({
            items: (itemContext, plugin) => {
                console.log('contextMenu', itemContext, plugin);
                if (itemContext instanceof WorkflowStepNode) {
                    return this.getStepNodeContextMenuItems(itemContext as WorkflowStepNode);
                }

                return {
                    searchBar: false,
                    list: [],
                };
            },
        });
        this.render.addPreset(RenderPresets.contextMenu.setup({ delay: 0 }));
        this.area.use(contextMenu);
    }

    /**
     * Initialise le compostement de la selection de l'editeur Rete.js.
     */
    private initEditorSelection() {
        const selector = AreaExtensions.selector();
        const accumulating = AreaExtensions.accumulateOnCtrl();

        AreaExtensions.selectableNodes(this.area, selector, { accumulating });
    }

    /**
     * Ajoute un noeud de type action dans l'éditeur (Méthode générique).
     */
    private async addActionNode(nodeName: string, actionId: number, actionType: WorkflowActionTypes, data: AnyActionData, position: { x: number; y: number }) {
        const workflowActionNode = new WorkflowActionNode(nodeName, actionType, data, this.callbackHandler.actionNodeCallBack.bind(this.callbackHandler));

        workflowActionNode.addInput(actionId.toString(), new ClassicPreset.Input(new ClassicPreset.Socket('unknow'), ''));
        await this.context.editor.addNode(workflowActionNode);
        await this.context.area.translate(workflowActionNode.id, { x: position.x, y: position.y });

        return workflowActionNode.id;
    }

    /**
     * Ajoute un noeud de type trigger dans l'éditeur (Méthode générique).
     */
    private async addTriggerNode(nodeName: string, triggerId: number, triggerType: WorkflowTriggerTypes, data: AnyTriggerData, position: { x: number; y: number }) {
        const workflowTriggerNode = new WorkflowTriggerNode(nodeName, triggerType, data, this.callbackHandler.triggerNodeCallBack.bind(this.callbackHandler));

        workflowTriggerNode.addOutput(triggerId.toString(), new ClassicPreset.Output(new ClassicPreset.Socket('unknow'), ''));
        await this.context.editor.addNode(workflowTriggerNode);
        await this.context.area.translate(workflowTriggerNode.id, { x: position.x, y: position.y });

        return workflowTriggerNode.id;
    }

    /**
     * Ajoute une connexion dans l'éditeur.
     * @param triggerNodeId : Id du noeud corespondant au trigger.
     * @param triggerOutputName : Nom de la socket du noeud utilisée comme origine de la connexion.
     * @param actionNodeId : Id du noeud corespondant à l'action.
     * @param actionInputName : Nom de la socket du noeud utilisée comme destination de la connexion.
     * @param data : Données de l'évènement associé à la connexion.
     * @param condition : Condition devant être respectée par l'évènement.
     * @param isEnabled : Valeur indiquant si l'évènement est actif.
     */
    async addConnection(triggerNodeId: string, triggerOutputName: Socket, actionNodeId: string, actionInputName: Socket, eventId: number, data: EventData) {
        const triggerNode = this.context.editor.getNodes().find((x) => x.id === triggerNodeId);
        const actionNode = this.context.editor.getNodes().find((x) => x.id === actionNodeId);
        let connection1 = new SelectableConnection(
            triggerNode,
            triggerOutputName,
            actionNode,
            actionInputName,
            eventId,
            data,
            this.callbackHandler.connectionCallBack.bind(this.callbackHandler)
        );
        await this.context.editor.addConnection(connection1);
    }

    /**
     * Affiche un workflow dans l'editeur Rete.js.
     * @param workflow : Instance du workflow à afficher.
     */
    async displayWorkflow(workflow: Workflow) {
        this.initAutoIncrementId(workflow);

        let stepActions = workflow.actions.filter(
            (action) => action.type === WorkflowActionTypes.ActivateStep || action.type === WorkflowActionTypes.CompleteStep || action.type === WorkflowActionTypes.ChangeStepState
        );

        let otherActions = workflow.actions.filter((act1) => !stepActions.some((act2) => act1.id === act2.id));

        let stepTriggers = workflow.triggers.filter(
            (trigger) =>
                trigger.type === WorkflowTriggerTypes.StepActivated || trigger.type === WorkflowTriggerTypes.StepCompleted || trigger.type === WorkflowTriggerTypes.StepStateChanged
        );

        let otherTriggers = workflow.triggers.filter((trig1) => !stepTriggers.some((trig2) => trig1.id === trig2.id));

        await this.buildStepNodes(workflow.steps, stepActions, stepTriggers);

        await this.buildTriggerNodes(otherTriggers);

        await this.buildActionNodes(otherActions);

        /* Adapte la taille pour voir l'ensemble des noeuds */
        setTimeout(() => {
            this.buildEventConnections(workflow.events);

            // wait until nodes rendered because they dont have predefined width and height
            AreaExtensions.zoomAt(this.area, this.editor.getNodes());

            this.subscribeToEditorEvents();
        }, 10);
    }

    /**
     * Construit les noeuds des étapes avec les sockets d'entrée correspondant aux actions et les sockets de sortie correspondant aux triggers..
     * @param steps : Liste des étapes.
     * @param stepActions : Liste des actions sur les étapes.
     * @param stepTriggers : Liste des triggers déclenchés par une étape.
     */
    async buildStepNodes(steps: Step[], stepActions: Action[], stepTriggers: Trigger[]) {
        steps.forEach((step) => {
            let inputs: StepInput[] = [];
            const currentStepActions = stepActions.filter((action) => (action.data as StepActionData).stepId === step.id);

            currentStepActions.forEach((action) => {
                if (this.existInputSocket(action, inputs, steps) === false) {
                    let input = this.createInputSocket(action, steps);
                    inputs.push(input);
                }
            });

            const outputs: StepOutput[] = [];
            const currentStepTriggers = stepTriggers.filter((trigger) => (trigger.data as StepTriggerData).stepId === step.id);

            currentStepTriggers.forEach((trigger) => {
                // TODO stocker le trigger dans un map pour retrouver l'id du trigger à partir de l'id du noeud de l'étape et le output
                if (this.existOutputSocket(trigger, outputs, steps) === false) {
                    let output = this.createOutputSocket(trigger, steps);
                    outputs.push(output);
                }
            });

            this.buildStepNode(this.context, step.data.stepName, inputs, outputs, step.position, step.data).then((nodeId) => {
                // this.nodeIdMappings.set(step.id, nodeId);
                this.mappingInfos.stepNodeIdMappings.set(nodeId, step.id);

                currentStepActions.forEach((action) => {
                    this.mappingInfos.actionNodeIdMappings.set(action.id, nodeId);
                    console.log(`add node ${nodeId} for step action ${action.id}}`);
                });

                currentStepTriggers.forEach((trigger) => {
                    this.mappingInfos.triggerNodeIdMappings.set(trigger.id, nodeId);
                    console.log(`add node ${nodeId} for step trigger ${trigger.id}}`);
                });

                inputs.forEach((input) => {
                    this.mappingInfos.setStepNodeActionId(nodeId, input.code, input.actionId);
                });

                outputs.forEach((output) => {
                    this.mappingInfos.setStepNodeTriggerId(nodeId, output.code, output.triggerId);
                });
            });
        });
    }

    /**
     * Construit le noeud d'une étape avec les sockets d'entrée correspondant aux actions et les sockets de sortie correspondant aux triggers..
     * @param context: Contexte d'affichage.
     * @param stepName : Nom de l'étape.
     * @param inputs : Liste des socket d'entrée du noeud.
     * @param outputs : Liste des socket de sortie du noeud.
     * @param data : Données de l'étape.
     */
    async buildStepNode(context: ReteContext, stepName: string, inputs: StepInput[], outputs: StepOutput[], position: { x: number; y: number }, data: StepData) {
        const workflowStepNode = new WorkflowStepNode(stepName, data, this.callbackHandler.stepNodeCallBack.bind(this.callbackHandler));

        inputs.forEach((input) => {
            workflowStepNode.addInput(input.code.toString(), new ClassicPreset.Input(new ClassicPreset.Socket(input.label), input.label));
        });

        outputs.forEach((output) => {
            workflowStepNode.addOutput(output.code.toString(), new ClassicPreset.Output(new ClassicPreset.Socket(output.label), output.label));
        });

        await this.editor.addNode(workflowStepNode);
        await this.area.translate(workflowStepNode.id, { x: position.x, y: position.y });
        console.log(`add node ${workflowStepNode.id} for step ${stepName}}`);
        return workflowStepNode.id;
    }

    /**
     * Construit les noeuds des triggers.
     * @param triggers : Liste des triggers.
     */
    async buildTriggerNodes(triggers: Trigger[]) {
        await triggers.forEach((trigger) => {
            let nodeName: string = null;

            switch (trigger.type) {
                case WorkflowTriggerTypes.AttributeChanged:
                    nodeName = `Mise à jour d'attribut`;
                    break;
                case WorkflowTriggerTypes.DateReached:
                    nodeName = `Date atteinte`;
                    break;
                case WorkflowTriggerTypes.DelayExpired:
                    nodeName = `Délai expiré`;
                    break;
                case WorkflowTriggerTypes.FolderCreated:
                    nodeName = `Création de dossier`;
                    break;
                case WorkflowTriggerTypes.Timer:
                    nodeName = `Minuteur`;
                    break;
            }

            this.addTriggerNode(nodeName, trigger.id, trigger.type, trigger.data, trigger.position).then((nodeId) => {
                console.log(`add node ${nodeId} for trigger ${trigger.id}}`);
                this.mappingInfos.triggerNodeIdMappings.set(trigger.id, nodeId);
            });
        });
    }

    /**
     * Construit les noeuds des actions.
     * @param actions : Liste des actions.
     */
    async buildActionNodes(actions: Action[]) {
        actions.forEach((action) => {
            let nodeName: string = null;

            switch (action.type) {
                case WorkflowActionTypes.ApiCall:
                    nodeName = `Appel d'API`;
                    break;
                case WorkflowActionTypes.CompleteWorkflow:
                    nodeName = `Finalisation du workflow`;
                    break;
                case WorkflowActionTypes.SendMail:
                    nodeName = `Envoi de mail`;
                    break;
                case WorkflowActionTypes.SendSms:
                    nodeName = `Envoi de SMS`;
                    break;
                case WorkflowActionTypes.SwitchFolderAsUnread:
                    nodeName = `Passage du dossier à non lu`;
                    break;
                case WorkflowActionTypes.UpdateAttribute:
                    nodeName = `Mise à jour d'attribut`;
                    break;
                case WorkflowActionTypes.TimerStop:
                    nodeName = `Arrêt de minuteur`;
                    break;
                case WorkflowActionTypes.ReminderCreation:
                    nodeName = `Création de rappel`;
                    break;
                case WorkflowActionTypes.ArchiveFolder:
                    nodeName = `Archivage de dossier`;
                    break;
            }

            this.addActionNode(nodeName, action.id, action.type, action.data, action.position).then((nodeId) => {
                console.log(`add node ${nodeId} for action ${action.id}}`);
                this.mappingInfos.actionNodeIdMappings.set(action.id, nodeId);
            });
        });
    }

    /**
     * Construit les connexions des évènements.
     * @param events : Liste des évènements.
     */
    async buildEventConnections(events: Event[]) {
        events.forEach((event) => {
            this.addConnection(
                this.mappingInfos.triggerNodeIdMappings.get(event.triggerId),
                event.triggerSocket,
                this.mappingInfos.actionNodeIdMappings.get(event.actionId),
                event.actionSocket,
                event.id,
                event.data
            );
        });
    }

    /**
     * Crée une socket de sortie d'une étape de workflow.
     * @param trigger : Triggers déclenché par l'étape
     * @returns : Une socket de sortie.
     */
    public createOutputSocket(trigger: Trigger, steps: Step[]) {
        let output = new StepOutput(trigger.id);
        switch (trigger.type) {
            case WorkflowTriggerTypes.StepActivated:
                output.label = 'Activée';
                output.code = trigger.id;
                break;
            case WorkflowTriggerTypes.StepCompleted:
                output.label = 'Finalisée';
                output.code = trigger.id;
                break;
            case WorkflowTriggerTypes.StepStateChanged:
                const stepStateChangedTriggerData = trigger.data as StepStateChangedTriggerData;
                const step = steps.find((x) => x.id == stepStateChangedTriggerData.stepId);
                const stateLabel = step.data.states.find((x) => x.id == stepStateChangedTriggerData.stateId).name;
                output.label = `Etat à '${stateLabel}'`;
                output.code = trigger.id;
                break;
        }
        return output;
    }

    private existOutputSocket(trigger: Trigger, outputs: StepOutput[], steps: Step[]) {
        const tempOutput = this.createOutputSocket(trigger, steps);

        if (outputs.find((x) => x.label == tempOutput.label)) {
            return true;
        } else {
            return false;
        }
    }

    private existInputSocket(action: Action, inputs: StepInput[], steps: Step[]) {
        const tempInput = this.createInputSocket(action, steps);

        if (inputs.find((x) => x.label == tempInput.label)) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Crée une socket d'entrée d'une étape de workflow.
     * @param trigger : Action sur l'étape
     * @returns : Une socket de sortie.
     */
    public createInputSocket(action: Action, steps: Step[]) {
        let input = new StepInput(action.id);

        switch (action.type) {
            case WorkflowActionTypes.ActivateStep:
                input.label = 'Activation';
                input.code = action.id;
                break;
            case WorkflowActionTypes.CompleteStep:
                input.label = 'Finalisation';
                input.code = action.id;
                break;
            case WorkflowActionTypes.ChangeStepState:
                const changeStepStateActionData = action.data as ChangeStepStateActionData;
                const step = steps.find((x) => x.id == changeStepStateActionData.stepId);
                const stateLabel = step.data.states.find((x) => x.id == changeStepStateActionData.stateId).name;
                input.label = `Changer l'état à '${stateLabel}'`;
                input.code = action.id;
                break;
        }
        return input;
    }

    initAutoIncrementId(workflow: Workflow) {
        // Cherche le prochain ID par rapport aux triggers existants
        this.context.workflow.triggers.forEach((trigger: Trigger) => {
            if (this.nextTriggerId <= trigger.id) {
                this.nextTriggerId = trigger.id + 1;
            }
        });

        // Cherche le prochain ID par rapport aux actions existantes
        this.context.workflow.actions.forEach((action: Action) => {
            if (this.nextActionId <= action.id) {
                this.nextActionId = action.id + 1;
            }
        });

        // Cherche le prochain ID par rapport aux étapes existantes
        this.context.workflow.steps.forEach((step: Step) => {
            if (this.nextStepId <= step.id) {
                this.nextStepId = step.id + 1;
            }
        });

        // Cherche le prochain ID par rapport aux évènement existants
        this.context.workflow.events.forEach((evt: Event) => {
            if (this.nextEventId <= evt.id) {
                this.nextEventId = evt.id + 1;
            }
        });
    }

    /**
     * Genere le prochain Id d'un trigger.
     */
    public generateNewTriggerId() {
        return this.nextTriggerId++;
    }

    /**
     * Genere le prochain Id d'un trigger.
     */
    public generateNewActionId() {
        return this.nextActionId++;
    }

    /**
     * Genere le prochain Id d'une étape.
     */
    public generateNewStepId() {
        return this.nextStepId++;
    }

    /**
     * Genere le prochain Id d'un évènement.
     */
    public generateNewEventId() {
        return this.nextEventId++;
    }

    /**
     * Retourne les elements du menu contextuel d'un noeud étape.
     * @returns les elements du menu contextuel.
     */
    getStepNodeContextMenuItems(itemContext: WorkflowStepNode) {
        return {
            searchBar: false,
            delay: 0,
            list: [
                {
                    label: 'Ajouter un trigger',
                    key: '1',
                    handler: () => null,
                    subitems: [
                        { label: "Trigger d'activation d'étape", key: '1', handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddTriggerActivated)) },
                        {
                            label: "Trigger de finalisation d'étape",
                            key: '2',
                            handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddTriggerCompleted)),
                        },
                        {
                            label: "trigger de changement d'état",
                            key: '3',
                            handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddTriggerStepChanged)),
                        },
                    ],
                },
                {
                    label: 'Ajouter une action',
                    key: '2',
                    handler: () => null,
                    subitems: [
                        { label: "Activer l'étape", key: '1', handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddActionActivate)) },
                        { label: "Finaliser l'étape", key: '2', handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddActionComplete)) },
                        { label: "Changer l'état", key: '3', handler: () => itemContext.callBack(itemContext.id, new NodeEvent(NodeEventTypes.AddActionChangeStep)) },
                    ],
                },
            ],
        };
    }
}
